import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { App, Modal, Select } from 'antd';

import { viewAsApi, type WikiMeDto } from '../lib/authApi';

/**
 * «Смотреть от лица партнёра» в FISHKA HUB (владелец 07.10.2026) — как в CRM
 * (frontend/src/components/ViewAs.tsx): те же эндпоинты /auth/view-as, тот же
 * режим в общей сессии. Только просмотр: любую запись сервер отбивает
 * (WikiViewerGuard → 403 «Режим просмотра — только чтение»). После входа и
 * выхода — полная перезагрузка: меню, права и кэши считаются заново.
 */

/** Куда вернуться после переключения: админку партнёр не видит — тогда на главную. */
function reloadTarget(): string {
  const { pathname, search } = window.location;
  return pathname.startsWith('/admin') ? '/' : `${pathname}${search}`;
}

/** Выйти из режима просмотра (плашка и меню профиля). */
export async function stopViewAs(): Promise<void> {
  await viewAsApi.stop();
  window.location.assign(reloadTarget());
}

/** Строка поиска с задержкой — не дёргаем сервер на каждую букву. */
function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

const errText = (e: unknown): string => (e instanceof Error ? e.message : 'Не получилось');

/** Окно выбора: роль (по умолчанию — партнёр) и ИП. */
export function ViewAsModal({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { message } = App.useApp();
  const [roleId, setRoleId] = useState<string>();
  const [partnerId, setPartnerId] = useState<string>();
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 300);
  const [busy, setBusy] = useState(false);

  const opts = useQuery({ queryKey: ['view-as', 'options'], queryFn: () => viewAsApi.options(), enabled: open, staleTime: 60_000, retry: false });
  const roles = useMemo(() => opts.data?.roles ?? [], [opts.data]);
  // Пункт меню — «от лица партнёра»: роль партнёра выбрана сразу.
  useEffect(() => {
    if (roleId || roles.length === 0) return;
    const partnerRole = roles.find((r) => r.isPartner);
    if (partnerRole) setRoleId(partnerRole.id);
  }, [roles, roleId]);
  const role = roles.find((r) => r.id === roleId);

  const partners = useQuery({
    queryKey: ['view-as', 'partners', dq],
    queryFn: () => viewAsApi.partners(dq),
    enabled: open && !!role?.isPartner,
    staleTime: 60_000,
    retry: false,
  });

  const start = async (): Promise<void> => {
    if (!roleId) return;
    setBusy(true);
    try {
      await viewAsApi.start(roleId, role?.isPartner ? partnerId : undefined);
      window.location.assign(reloadTarget());
    } catch (e) {
      message.error(errText(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Смотреть от лица партнёра"
      okText="Смотреть"
      cancelText="Отмена"
      okButtonProps={{ disabled: !roleId || (role?.isPartner && !partnerId), loading: busy }}
      onOk={() => void start()}
      destroyOnHidden
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13.5, color: 'var(--fk-text-2)', lineHeight: 1.5 }}>
          Вы увидите меню, разделы и «Мои задачи» так, как их видит партнёр. Только просмотр: отметить, отправить или изменить ничего
          нельзя. Режим общий с CRM — выйти можно здесь или там.
        </div>
        {opts.isError ? (
          <div style={{ fontSize: 13, color: 'var(--fk-error)' }}>{errText(opts.error)}</div>
        ) : null}
        <Select
          placeholder="Роль"
          loading={opts.isLoading}
          value={roleId}
          onChange={(v: string) => {
            setRoleId(v);
            setPartnerId(undefined);
          }}
          options={roles.map((r) => ({ value: r.id, label: r.name }))}
          showSearch={{ optionFilterProp: 'label' }}
          style={{ width: '100%' }}
          notFoundContent="Нет ролей, от лица которых вам можно смотреть"
        />
        {role?.isPartner ? (
          <Select
            placeholder="Партнёр (ИП) — начните вводить название"
            value={partnerId}
            onChange={(v: string) => setPartnerId(v)}
            loading={partners.isFetching}
            options={(partners.data ?? []).map((p) => ({ value: p.id, label: p.isActive ? p.name : `${p.name} · не активен` }))}
            showSearch={{ filterOption: false, onSearch: setQ, searchValue: q }}
            style={{ width: '100%' }}
            notFoundContent={partners.isFetching ? 'Ищем…' : 'Не нашли — уточните название'}
          />
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Жёлтая плашка под шапкой, пока включён режим просмотра: от лица кого
 * смотрим + «Выйти». Липкая — видна при прокрутке.
 */
export function ViewAsBanner({ me }: { me: WikiMeDto | undefined }): JSX.Element | null {
  const { message } = App.useApp();
  const [busy, setBusy] = useState(false);
  const va = me?.viewAs;
  if (!va) return null;
  // «От лица пользователя» (12.10.2026): «ФИО» (роль «…»); режим роли — как раньше.
  const who = va.userName ? `«${va.userName}»` : va.partnerName || va.roleName;
  const stop = async (): Promise<void> => {
    setBusy(true);
    try {
      await stopViewAs();
    } catch (e) {
      message.error(errText(e));
      setBusy(false);
    }
  };
  return (
    <div className="fk-hub-viewas" role="status">
      <EyeOutlined className="fk-hub-viewas-ic" />
      <span className="fk-hub-viewas-text">
        {va.userName ? 'Вы смотрите от лица ' : 'Вы смотрите как '}
        <b>{who}</b>
        {va.userName ? (
          <span className="fk-hub-viewas-role"> (роль «{va.roleName}»)</span>
        ) : va.partnerName ? (
          <span className="fk-hub-viewas-role"> · {va.roleName}</span>
        ) : null}{' '}
        — только чтение (кроме переписки по помещению: пишете от имени партнёра)
      </span>
      <button
        type="button"
        className="fk-btn fk-btn-sm fk-hub-viewas-exit"
        onClick={() => void stop()}
        disabled={busy}
        title={`Вернуться к себе — ${va.realName}`}
        aria-label="Выйти из режима просмотра"
      >
        {busy ? 'Выходим…' : 'Выйти'}
      </button>
    </div>
  );
}
